/* import __COLOCATED_TEMPLATE__ from './suggestion-editor.hbs'; */
/* RESPONSIBLE TEAM: team-conversational-knowledge */

import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Block } from '@intercom/interblocks.ts';
import type ContentReviewRequest from 'embercom/models/content-service/content-review-request';
import type { LocalizedKnowledgeContent } from 'embercom/objects/knowledge-hub/localized-knowledge-content';
import type Model from '@ember-data/model';
import { inject as service } from '@ember/service';
import type SuggestionEditorService from 'embercom/services/suggestion-editor-service';
import { EntityType } from 'embercom/models/data/entity-types';
import { type ReviewType } from 'embercom/models/content-service/content-review-request';

interface SuggestionEditorArgs {
  reviewRequests: ContentReviewRequest[];
  activeContent: LocalizedKnowledgeContent & Model;
  updateSuggestionBlocks: (blocks: Block[]) => void;
  setRejectedContentReviewRequestId: (id: string) => void;
  setApprovedContentReviewRequestId: (id: string) => void;
  approvedContentReviewRequestIds: Set<string>;
}

export type BlockGroup = {
  id: string;
  isEdited: boolean;
  blocksBefore?: Block[];
  blocks: Block[];
  indices: [number, number];
  reviewReason?: string;
  suggestionCreatedAt?: Date;
  includedConversationIds?: number[];
  contentReviewRequestId?: string;
  reviewType?: ReviewType;
};

export default class SuggestionEditor extends Component<SuggestionEditorArgs> {
  @service declare appService: $TSFixMe;
  @service declare suggestionEditorService: SuggestionEditorService;

  @tracked showPreviousVersion = false;

  @tracked declare updatedBlocksForIndex: Block[];

  constructor(owner: any, args: SuggestionEditorArgs) {
    super(owner, args);
    this.suggestionEditorService.createBlockGroups(
      this.args.activeContent,
      this.args.reviewRequests,
    );
    this.args.updateSuggestionBlocks(
      Object.values(this.suggestionEditorService.blockGroupsForEditing).flat(),
    );
  }

  get isArticleContent() {
    return this.args.activeContent.entityType === EntityType.ArticleContent;
  }

  get blockGroups(): BlockGroup[] {
    return this.suggestionEditorService.blockGroups;
  }

  @action
  scrollToFirstEditedBlockGroup() {
    if (this.editedBlockGroups.length > 0) {
      this.scrollToBlockGroup(this.editedBlockGroups[0]);
    }
  }

  scrollToBlockGroup(blockGroup: BlockGroup) {
    let blockGroupElement = document.getElementById(blockGroup.id);
    if (blockGroupElement) {
      blockGroupElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  get editedBlockGroups(): BlockGroup[] {
    return this.blockGroups.filter((group) => group.isEdited);
  }

  get uneditedBlockGroups(): BlockGroup[] {
    return this.blockGroups.filter((group) => !group.isEdited);
  }

  get suggestionsToEditBlockGroups(): BlockGroup[] {
    return this.blockGroups;
  }

  @action
  toggleShowPreviousVersion() {
    this.showPreviousVersion = !this.showPreviousVersion;
  }

  @action
  updateBlocksForBlockGroup(blockGroup: BlockGroup, blocks: Block[]) {
    this.suggestionEditorService.blockGroupsForEditing[`${blockGroup.indices}`] = blocks;

    this.args.updateSuggestionBlocks(
      Object.values(this.suggestionEditorService.blockGroupsForEditing).flat(),
    );
    if (blockGroup.id.startsWith('edited') && blocks !== blockGroup.blocksBefore) {
      // Editing a suggestion means the suggestion has been approved
      this.args.setApprovedContentReviewRequestId(blockGroup.contentReviewRequestId!);
    }
  }

  @action
  handleBlockGroupSuggestion(blockGroup: BlockGroup, acceptSuggestion: boolean) {
    let hasApprovedSuggestion = this.args.approvedContentReviewRequestIds.has(
      blockGroup.contentReviewRequestId!,
    );

    // Needed to trigger a re-render of the block groups
    let updatedBlockGroups = this.blockGroups.map((group) => {
      if (group.id === blockGroup.id) {
        let currentBlocks =
          this.suggestionEditorService.blockGroupsForEditing[`${blockGroup.indices}`];

        let blocks = hasApprovedSuggestion ? currentBlocks : group.blocks;

        return {
          ...group,
          blocks: acceptSuggestion ? blocks : group.blocksBefore!,
          isEdited: false,
        };
      }
      return group;
    });
    this.suggestionEditorService.blockGroups = updatedBlockGroups;

    if (!acceptSuggestion) {
      // If rejecting a suggestion, we need to revert to previous blocks before the suggestion
      this.updateBlocksForBlockGroup(blockGroup, blockGroup.blocksBefore!);
      this.args.setRejectedContentReviewRequestId(blockGroup.contentReviewRequestId!);
    } else if (acceptSuggestion && !hasApprovedSuggestion) {
      this.updateBlocksForBlockGroup(blockGroup, blockGroup.blocks);
      this.args.setApprovedContentReviewRequestId(blockGroup.contentReviewRequestId!);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::Shared::SuggestionEditor': typeof SuggestionEditor;
  }
}
